import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pillarQuestion } from '../../utils/artworkInput';
import { registerArtworkControls } from '../../utils/artworkControlsMenu';
import './ArtworkControls.css';

const emptyStatus = { ready: false, mode: '', busy: false, values: {}, question: '' };
const touchPrimary = () => window.matchMedia('(pointer: coarse)').matches;

export default function ArtworkControls({ getControls, projectId }) {
  const [touch, setTouch] = useState(touchPrimary);
  const [expanded, setExpanded] = useState(touchPrimary);
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState(emptyStatus);
  const [localMode, setLocalMode] = useState('');
  const [question, setQuestion] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [viewport, setViewport] = useState({ inset: 0, height: window.innerHeight });
  const panel = useRef(null), input = useRef(null), handle = useRef(null), idle = useRef(null), exportTimer = useRef(null);
  const getter = useRef(getControls); getter.current = getControls;
  const lastSnapshot = useRef('');
  const pinnedState = useRef(false);
  const sample = useCallback(() => {
    const controls = getter.current();
    if (!controls) return;
    try {
      const nextProfile = controls.profile(), nextStatus = controls.snapshot();
      const serialized = JSON.stringify([nextProfile, nextStatus]);
      if (serialized !== lastSnapshot.current) {
        lastSnapshot.current = serialized;
        setProfile(nextProfile); setStatus(nextStatus);
      }
    } catch (e) { setError('These controls could not be read.'); }
  }, []);
  const mode = (profile && profile.source === 'reviewed') || status.managedMode ? status.mode : localMode;
  const active = profile && profile.actions.find(a => a.id === mode);
  const busy = status.busy || exporting;
  const pinned = !!mode || saveOpen || busy;
  pinnedState.current = pinned;
  const visible = expanded || (touch && !!mode);
  const hasControls = status.ready && profile && (profile.actions.length > 0 || profile.exports.length > 0);

  const wake = useCallback(() => {
    window.clearTimeout(idle.current);
    idle.current = window.setTimeout(() => {
      if (pinnedState.current) return;
      if (panel.current && panel.current.contains(document.activeElement)) return;
      setExpanded(false);
    }, 4000);
  }, []);
  useEffect(() => {
    sample();
    const timer = window.setInterval(sample, 150);
    return () => window.clearInterval(timer);
  }, [sample]);
  useEffect(() => {
    const media = window.matchMedia('(pointer: coarse)');
    const change = () => { setTouch(media.matches); setExpanded(media.matches); };
    const point = event => {
      if (event.pointerType === 'touch') { setTouch(true); setExpanded(true); wake(); }
    };
    if (media.addEventListener) media.addEventListener('change', change); else media.addListener(change);
    window.addEventListener('pointerdown', point, { passive: true });
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', change); else media.removeListener(change);
      window.removeEventListener('pointerdown', point);
    };
  }, [wake]);
  useEffect(() => {
    const view = window.visualViewport;
    const resize = () => setViewport({ inset: view ? Math.max(0, window.innerHeight - view.height - view.offsetTop) : 0,
      height: view ? view.height : window.innerHeight });
    resize();
    if (view) { view.addEventListener('resize', resize); view.addEventListener('scroll', resize); }
    window.addEventListener('resize', resize);
    return () => {
      if (view) { view.removeEventListener('resize', resize); view.removeEventListener('scroll', resize); }
      window.removeEventListener('resize', resize);
    };
  }, []);
  useEffect(() => {
    if (!hasControls) return undefined;
    return registerArtworkControls(() => { setExpanded(true); wake(); });
  }, [hasControls, wake]);
  useEffect(() => {
    window.clearTimeout(idle.current);
    if (hasControls && visible && !pinned) wake();
    return () => window.clearTimeout(idle.current);
  }, [hasControls, visible, pinned, wake]);
  useEffect(() => () => window.clearTimeout(exportTimer.current), []);

  const invoke = operation => {
    try { setError(''); operation(getter.current()); sample(); }
    catch (e) { setError('This action could not finish. Please try again.'); }
  };
  const close = () => {
    invoke(controls => {
      if (profile.source === 'declared' && mode) controls.input(mode, { action: 'close' });
      else controls.close();
    });
    setLocalMode(''); setQuestion(''); setSaveOpen(false);
    if (input.current) input.current.blur();
    wake();
  };
  const activate = action => {
    if (busy) return;
    if (mode && profile.source === 'declared') invoke(c => c.input(mode, { action: 'close' }));
    invoke(c => c.action(action.id));
    setLocalMode(['text', 'navigation'].includes(action.type) ? action.id : '');
    setQuestion('');
    if (action.type === 'text' && input.current) input.current.focus();
    else if (input.current) input.current.blur();
    wake();
  };
  const updateQuestion = value => {
    const text = profile.interaction === 'pillars' ? pillarQuestion(value) : value.slice(0, (active && active.maxLength) || 120);
    setQuestion(text);
    invoke(c => profile.interaction === 'pillars' ? c.question(text) : c.input(mode, { action: 'change', text }));
  };
  const submit = event => {
    event.preventDefault();
    if (!question.trim() || busy) return;
    invoke(c => {
      if (profile.interaction === 'pillars') { c.question(question); c.key('Enter'); }
      else c.input(mode, { action: 'submit', text: question });
    });
    setQuestion(''); input.current.blur();
  };
  const navigate = (action, key) => invoke(c => profile.interaction === 'pillars' ? c.key(key) : c.input(mode, { action }));
  const save = action => {
    setExporting(true);
    // Paint the busy state before the artwork starts its synchronous capture.
    exportTimer.current = window.setTimeout(() => {
      invoke(c => c.action(action.id));
      setExporting(false);
    }, 30);
  };

  if (!hasControls) return null;
  return createPortal(
    <aside className={`artwork-controls ${visible ? 'is-open' : 'is-idle'} ${touch ? 'is-touch' : 'is-desktop'}`}
      data-artwork-controls="panel" data-project-id={projectId} aria-label="Artwork controls"
      style={{ '--artwork-keyboard-inset': `${viewport.inset}px`, '--artwork-viewport-height': `${viewport.height}px` }}
      onPointerDown={() => { if (!pinned) wake(); }} onClick={e => {
        e.stopPropagation();
        // A touch leaves buttons focused in some browsers. It must not pin a
        // finished action forever; keyboard focus still prevents fading.
        const button = e.target.closest('button');
        if (e.detail > 0 && button) button.blur();
      }}
      onPointerMove={e => { if (e.pointerType === 'mouse' && !pinned) wake(); }}
      onKeyDown={e => e.stopPropagation()}>
      <button ref={handle} type="button" className="btn btn-outline-secondary btn-sm font-display artwork-controls-handle"
        aria-label="Artwork controls" title="Artwork controls"
        aria-expanded={visible} aria-controls="artwork-control-panel" onClick={() => { setExpanded(true); wake(); }}
        hidden={visible}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M3 6h4m4 0h10M3 12h10m4 0h4M3 18h4m4 0h10" />
          <path d="M7 3h4v6H7zM13 9h4v6h-4zM7 15h4v6H7z" />
        </svg>
      </button>
      <div ref={panel} id="artwork-control-panel" className="artwork-controls-panel modal-content" aria-hidden={!visible}
        onFocus={() => window.clearTimeout(idle.current)} onBlur={() => { if (!pinned) wake(); }}>
        <div className="artwork-controls-actions">
          {profile.actions.map(action => <button key={action.id} data-action={action.id} type="button"
            className={`btn btn-sm font-display artwork-control ${(action.type === 'toggle' && status.values[action.id]) || mode === action.id ? 'btn-secondary' : 'btn-outline-secondary'}`}
            disabled={busy} tabIndex={visible ? 0 : -1}
            aria-pressed={action.type === 'toggle' ? !!status.values[action.id] : ['text', 'navigation'].includes(action.type) ? mode === action.id : undefined}
            onClick={() => activate(action)}>{action.label}{action.key && profile.source !== 'detected' && ` (${action.key.toUpperCase()})`}</button>)}
          {profile.exports.length > 0 && <button type="button" className="btn btn-outline-secondary btn-sm font-display artwork-controls-save"
            disabled={busy} tabIndex={visible ? 0 : -1} aria-expanded={saveOpen} onClick={() => setSaveOpen(!saveOpen)}>Save</button>}
          {mode && <button type="button" className="btn btn-outline-secondary btn-sm font-display" data-action="close"
            disabled={busy} tabIndex={visible ? 0 : -1} onClick={close}>Close</button>}
          {!mode && <button type="button" className="btn btn-outline-secondary btn-sm artwork-controls-hide" aria-label="Hide artwork controls"
            disabled={busy} tabIndex={visible ? 0 : -1} onClick={() => {
              setSaveOpen(false); setExpanded(false);
              window.requestAnimationFrame(() => {
                const target = handle.current;
                if (target) target.focus();
              });
            }}>×</button>}
        </div>
        {saveOpen && <div className="artwork-controls-exports">
          {profile.exports.map(action => <button key={action.id} type="button" data-action={action.id}
            className="btn btn-secondary btn-sm font-display" disabled={busy} onClick={() => save(action)}>{action.label}{action.key && ` (${action.key.toUpperCase()})`}</button>)}
        </div>}
        {/* Keep the input mounted and focusable during the initial iOS gesture. */}
        <form onSubmit={submit} aria-hidden={!(active && active.type === 'text')} className={`artwork-controls-form ${active && active.type === 'text' ? '' : 'artwork-controls-form-closed'}`}>
          <label htmlFor="artwork-question" className="artwork-controls-label">{profile.interaction === 'pillars' ? 'Consult your PILLAR' : active ? active.label : 'Message'}</label>
          <div className="input-group artwork-controls-input">
            <input id="artwork-question" className="form-control" ref={input} type="text" inputMode="text" enterKeyHint="send"
              value={question} autoComplete="off" spellCheck="false" disabled={busy}
              maxLength={profile.interaction === 'pillars' ? 60 : (active && active.maxLength) || 120}
              placeholder="What is on your mind?" tabIndex={visible && active && active.type === 'text' ? 0 : -1}
              onChange={e => updateQuestion(e.target.value)} />
            <button type="submit" className="btn btn-secondary font-display" disabled={busy || !question.trim()}
              tabIndex={visible && active && active.type === 'text' ? 0 : -1}>Send</button>
          </div>
          {profile.interaction === 'pillars' && <p className="artwork-controls-hint">60 characters. Its reply appears on the stone.</p>}
        </form>
        {active && active.type === 'navigation' && <div className="artwork-controls-navigation">
          <button type="button" className="btn btn-outline-secondary" disabled={busy} aria-label="Previous row" onClick={() => navigate('up', 'ArrowUp')}>↑</button>
          <button type="button" className="btn btn-outline-secondary" disabled={busy} aria-label="Next row" onClick={() => navigate('down', 'ArrowDown')}>↓</button>
          <button type="button" className="btn btn-outline-secondary font-display" disabled={busy} onClick={() => navigate('open', 'Enter')}>Open</button>
          <button type="button" className="btn btn-outline-secondary font-display" onClick={() => navigate('back', 'Escape')}>Back</button>
        </div>}
        <div role="status" className="artwork-controls-status">{error || (exporting ? 'Preparing your image…' : status.busy ? 'Working…' : '')}</div>
      </div>
    </aside>, document.getElementById('root') || document.body);
}
